import { abs } from "@suse-o11y/abs";
import {
	type Interval,
	addMilliseconds,
	differenceInMilliseconds,
	isAfter,
	isBefore,
	isWithinInterval,
} from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { floor } from "@suse-o11y/floor";
import { min } from "@suse-o11y/min";
import { isNonEmptyArray } from "@suse-o11y/non-empty-array";
import { sign } from "@suse-o11y/sign";

import { DataPoint } from ".";

function findMinimalStep(points: ReadonlyArray<DataPoint>): number {
	// there are only N-1 steps between N points, so ignore the first one
	const [_ignored, ...steps] = points.map(([time], index, arr) => {
		const prev = arr.at(index - 1);

		if (prev == null) {
			return Number.POSITIVE_INFINITY;
		}

		return differenceInMilliseconds(time, prev[0]);
	});

	// return 0 as it is a possible worst case number which should be gracefully handled
	return min(steps) ?? 0;
}

function adjustBoundaryByStep(time: Time, boundary: Time, step: number): Time {
	// zero step means no adjustment
	if (step === 0) {
		return boundary;
	}

	const diff = differenceInMilliseconds(boundary, time);

	// use absolute value so it floors to the nearest step (-5.9 -> 5, 5.9 -> 5)
	const fullStepsInDiff = floor(abs(diff) / step, 0);

	// preserve the sign so it would subtract when the diff is negative
	return addMilliseconds(time, step * sign(diff) * fullStepsInDiff);
}

/**
 * !Purely visual driven adjustment!
 *
 * The function adjusts the data points array to only include points within the specified time interval.
 * If the interval wider, it adds null values for missing (start/end) points.
 * The added data points have the timestamps with respect to the minimal step between the present data points.
 *
 * It is necessary so charts render the gaps in the data within interval.
 */
export function dataPointsInInterval(points: ReadonlyArray<DataPoint>, interval: Interval): Array<DataPoint> {
	const pointsWithinInterval = points.filter(([time]) => isWithinInterval(time, interval));

	if (!isNonEmptyArray(pointsWithinInterval)) {
		return [];
	}

	// the first and last elements are always present at this point
	const [firstPointTime] = pointsWithinInterval[0];
	const [lastPointTime] = pointsWithinInterval[pointsWithinInterval.length - 1];

	// find minimal step among all of the points
	const minStep = findMinimalStep(points);

	const adjustedFirstPointTime = adjustBoundaryByStep(firstPointTime, interval.start, minStep);
	const adjustedLastPointTime = adjustBoundaryByStep(lastPointTime, interval.end, minStep);

	return [
		...(isBefore(adjustedFirstPointTime, firstPointTime) ? [DataPoint(adjustedFirstPointTime, null)] : []),
		...pointsWithinInterval,
		...(isAfter(adjustedLastPointTime, lastPointTime) ? [DataPoint(adjustedLastPointTime, null)] : []),
	];
}
